import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton, Badge, Menu, Box, Avatar, Typography, CircularProgress, Divider, Button,
} from '@mui/material';
import { NotificationsNone as BellIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import {
  apiGetNotifications, apiNotificationUnreadCount, apiMarkNotificationsRead,
  type NotificationItem,
} from '../../services/api';
import { subscribeToSocket } from '../../services/socket';
import { formatTimeAgo } from '../../utils/formatTimeAgo';
import { initials } from '../../utils/strings';

const AVATAR_GRAD = 'linear-gradient(140deg,#FFC24B 0%,#FF7A36 60%,#FF5B2E 100%)';
const POLL_MS = 60000;

function describe(n: NotificationItem): string {
  if (n.message) return n.message;
  const who = n.actor?.displayName || 'Someone';
  switch (n.type) {
    case 'friend_request': return `${who} sent you a friend request`;
    case 'friend_accept': return `${who} accepted your friend request`;
    case 'post_like': return `${who} liked your post`;
    case 'post_comment': return `${who} commented on your post`;
    case 'comment_reply': return `${who} replied to your comment`;
    default: return `${who} interacted with you`;
  }
}

function targetFor(n: NotificationItem): string {
  switch (n.type) {
    case 'friend_request':
    case 'friend_accept':
      return n.actor?.id ? `/profile/${n.actor.id}` : '/profile';
    default:
      // Likes/comments/replies deep-link to the post (Home pins + highlights it).
      return n.entityType === 'post' && n.entityId ? `/?post=${n.entityId}` : '/';
  }
}

const NotificationsBell: React.FC = () => {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [count, setCount] = React.useState(0);
  const [items, setItems] = React.useState<NotificationItem[] | null>(null);
  const open = Boolean(anchorEl);

  // Initial unread count + polling fallback (in case a socket event is missed).
  React.useEffect(() => {
    if (!me) { setCount(0); setItems(null); return; }
    let alive = true;
    const tick = () => { apiNotificationUnreadCount().then((r) => { if (alive) setCount(r.unread ?? 0); }).catch(() => {}); };
    tick();
    const id = window.setInterval(tick, POLL_MS);
    return () => { alive = false; window.clearInterval(id); };
  }, [me]);

  // Realtime: new notification + authoritative unread count from the server.
  React.useEffect(() => {
    if (!me) return;
    const offNew = subscribeToSocket('notification', (payload: NotificationItem) => {
      // Prepend to the open list if loaded; the badge number is owned by the authoritative
      // 'notification_count' event (and the poll) — incrementing here too would double-count
      // / drift on reconnects.
      setItems((prev) => (prev ? [payload, ...prev.filter((n) => n.id !== payload.id)] : prev));
    });
    const offCount = subscribeToSocket('notification_count', (payload: { unread: number }) => {
      if (typeof payload?.unread === 'number') setCount(payload.unread);
    });
    return () => { offNew(); offCount(); };
  }, [me]);

  const handleOpen = async (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setItems(null);
    try {
      const res = await apiGetNotifications(undefined, 20);
      setItems(res.items ?? []);
    } catch {
      setItems([]);
    }
    // Mark read once the panel is opened, then reconcile with the authoritative server
    // count (rather than forcing 0) so unread items beyond the first page — or any that
    // arrive during the open — are still reflected.
    if (count > 0) {
      apiMarkNotificationsRead()
        .then(() => {
          // Reflect read state in the open list immediately (the rows were fetched as unread).
          setItems((prev) => (prev ? prev.map((n) => ({ ...n, read: true })) : prev));
          return apiNotificationUnreadCount();
        })
        .then((r) => setCount(r.unread ?? 0))
        .catch(() => {});
    }
  };

  const handleClose = () => setAnchorEl(null);

  const handleClick = (n: NotificationItem) => {
    handleClose();
    navigate(targetFor(n));
  };

  if (!me) return null;

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}>
        <Badge badgeContent={count} color="error" max={99} overlap="circular">
          <BellIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '92vw', maxHeight: 460 } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Notifications</Typography>
        </Box>
        <Divider />

        {items === null && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
        )}

        {items !== null && items.length === 0 && (
          <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
          </Box>
        )}

        {items !== null && items.map((n) => (
          <Box
            key={n.id}
            role="button"
            tabIndex={0}
            onClick={() => handleClick(n)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(n); } }}
            sx={{
              display: 'flex', alignItems: 'flex-start', gap: 1.25, px: 2, py: 1.25, cursor: 'pointer',
              bgcolor: n.read ? 'transparent' : 'action.hover',
              borderLeft: '3px solid',
              borderLeftColor: n.read ? 'transparent' : 'primary.main',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <Avatar src={n.actor?.profileImage || undefined} sx={{ width: 36, height: 36, background: AVATAR_GRAD, color: '#fff', fontWeight: 700, fontSize: 14 }}>
              {initials(n.actor?.displayName || '')}
            </Avatar>
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600, lineHeight: 1.35 }}>
                {describe(n)}
              </Typography>
              <Typography variant="caption" color="text.secondary">{formatTimeAgo(n.createdAt)}</Typography>
            </Box>
          </Box>
        ))}

        {items !== null && items.length > 0 && (
          <>
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.75 }}>
              <Button size="small" onClick={handleClose}>Close</Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationsBell;
